import React, { useEffect, useRef, useState } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { Download, Copy, RefreshCw, Settings2, Palette, Square, Circle } from 'lucide-react';
import { toast } from 'sonner';

const QRCodeGenerator = ({ 
  tagId, 
  baseUrl = window.location.origin,
  size = 300,
  onClose 
}) => {
  const qrRef = useRef(null);
  const qrCodeInstance = useRef(null);
  const [isGenerating, setIsGenerating] = useState(true);
  
  // QR Code customization options
  const [options, setOptions] = useState({
    dotStyle: 'rounded',
    cornerStyle: 'extra-rounded',
    dotColor: '#6366f1',
    backgroundColor: '#ffffff',
    cornerDotColor: '#4f46e5',
    cornerSquareColor: '#6366f1',
    hasGradient: false,
    gradientType: 'linear',
    gradientColor1: '#6366f1',
    gradientColor2: '#ec4899',
    logoEnabled: false,
  });

  // Generate the QR code URL with source tracking
  const qrUrl = `${baseUrl}/${tagId}?source=qr`;

  // Dot style options
  const dotStyles = [
    { id: 'square', name: 'Square', icon: Square },
    { id: 'dots', name: 'Dots', icon: Circle },
    { id: 'rounded', name: 'Rounded', icon: Square },
    { id: 'classy', name: 'Classy', icon: Square },
    { id: 'classy-rounded', name: 'Classy Rounded', icon: Square },
    { id: 'extra-rounded', name: 'Extra Rounded', icon: Circle },
  ];

  // Corner style options
  const cornerStyles = [
    { id: 'square', name: 'Square' },
    { id: 'dot', name: 'Dot' },
    { id: 'extra-rounded', name: 'Rounded' },
  ];

  // Preset color schemes
  const presets = [
    { name: 'Indigo', dot: '#6366f1', corner: '#4f46e5', bg: '#ffffff' },
    { name: 'Emerald', dot: '#10b981', corner: '#059669', bg: '#ffffff' },
    { name: 'Rose', dot: '#f43f5e', corner: '#e11d48', bg: '#ffffff' },
    { name: 'Amber', dot: '#f59e0b', corner: '#d97706', bg: '#ffffff' },
    { name: 'Cyan', dot: '#06b6d4', corner: '#0891b2', bg: '#ffffff' },
    { name: 'Dark', dot: '#ffffff', corner: '#e5e7eb', bg: '#1f2937' },
  ];

  useEffect(() => {
    generateQRCode();
  }, [options, tagId]);

  const generateQRCode = () => {
    setIsGenerating(true);
    
    // Clear previous QR code
    if (qrRef.current) {
      qrRef.current.innerHTML = '';
    }

    // Build QR code options
    const qrOptions = {
      width: size,
      height: size,
      type: 'canvas',
      data: qrUrl,
      dotsOptions: {
        color: options.dotColor,
        type: options.dotStyle,
      },
      cornersSquareOptions: {
        color: options.cornerSquareColor,
        type: options.cornerStyle,
      },
      cornersDotOptions: {
        color: options.cornerDotColor,
        type: options.cornerStyle === 'extra-rounded' ? 'dot' : 'square',
      },
      backgroundOptions: {
        color: options.backgroundColor,
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 10,
      },
    };

    // Add gradient if enabled
    if (options.hasGradient) {
      qrOptions.dotsOptions.gradient = {
        type: options.gradientType,
        rotation: 45,
        colorStops: [
          { offset: 0, color: options.gradientColor1 },
          { offset: 1, color: options.gradientColor2 },
        ],
      };
    }

    // Create QR code instance
    qrCodeInstance.current = new QRCodeStyling(qrOptions);
    
    // Append to DOM
    if (qrRef.current) {
      qrCodeInstance.current.append(qrRef.current);
    }
    
    setIsGenerating(false);
  };

  const downloadQRCode = async (format) => {
    if (qrCodeInstance.current) {
      try {
        await qrCodeInstance.current.download({
          name: `QR-${tagId}`,
          extension: format,
        });
        toast.success(`QR code downloaded as ${format.toUpperCase()}`);
      } catch (error) {
        toast.error('Failed to download QR code');
      }
    }
  };

  const copyToClipboard = async () => {
    if (qrCodeInstance.current) {
      try {
        const blob = await qrCodeInstance.current.getRawData('png');
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        toast.success('QR code copied to clipboard!');
      } catch (error) {
        toast.error('Failed to copy QR code');
      }
    }
  };

  const applyPreset = (preset) => {
    setOptions(prev => ({
      ...prev,
      dotColor: preset.dot,
      cornerSquareColor: preset.dot,
      cornerDotColor: preset.corner,
      backgroundColor: preset.bg,
      hasGradient: false,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border/40">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">QR Code Generator</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Customize your QR code for tag: <span className="font-mono font-medium">{tagId}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-8">
          {/* QR Code Preview */}
          <div className="flex flex-col items-center">
            <div 
              ref={qrRef} 
              className="bg-white rounded-2xl p-4 shadow-lg"
              style={{ minWidth: size + 32, minHeight: size + 32 }}
            />

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6 justify-center">
              <button
                onClick={() => downloadQRCode('png')}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-opacity"
                data-testid="download-qr-png"
              >
                <Download className="w-4 h-4" />
                PNG
              </button>
              <button
                onClick={() => downloadQRCode('svg')}
                className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-full hover:bg-secondary/80 transition-colors"
                data-testid="download-qr-svg"
              >
                <Download className="w-4 h-4" />
                SVG
              </button>
              <button
                onClick={copyToClipboard}
                className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-full hover:bg-secondary/80 transition-colors"
                data-testid="copy-qr"
              >
                <Copy className="w-4 h-4" />
                Copy
              </button>
            </div>
          </div>

          {/* Customization Options */}
          <div className="space-y-6">
            {/* Color Presets */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-3">
                <Palette className="w-4 h-4" />
                Color Presets
              </label>
              <div className="flex flex-wrap gap-2">
                {presets.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => applyPreset(preset)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border/40 hover:border-primary/50 transition-colors text-sm"
                  >
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: preset.dot }}
                    />
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Dot Style */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-3">
                <Settings2 className="w-4 h-4" />
                Dot Style
              </label>
              <div className="grid grid-cols-3 gap-2">
                {dotStyles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setOptions(prev => ({ ...prev, dotStyle: style.id }))}
                    className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                      options.dotStyle === style.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border/40 hover:border-primary/50'
                    }`}
                  >
                    {style.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Corner Style */}
            <div>
              <label className="text-sm font-medium mb-3 block">Corner Style</label>
              <div className="grid grid-cols-3 gap-2">
                {cornerStyles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setOptions(prev => ({ ...prev, cornerStyle: style.id }))}
                    className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                      options.cornerStyle === style.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border/40 hover:border-primary/50'
                    }`}
                  >
                    {style.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Colors */}
            <div>
              <label className="text-sm font-medium mb-3 block">Custom Colors</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Dot Color</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={options.dotColor}
                      onChange={(e) => setOptions(prev => ({ 
                        ...prev, 
                        dotColor: e.target.value,
                        cornerSquareColor: e.target.value 
                      }))}
                      className="w-10 h-10 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={options.dotColor}
                      onChange={(e) => setOptions(prev => ({ 
                        ...prev, 
                        dotColor: e.target.value,
                        cornerSquareColor: e.target.value 
                      }))}
                      className="flex-1 px-3 py-2 rounded-lg border border-border/40 text-sm font-mono"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Background</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={options.backgroundColor}
                      onChange={(e) => setOptions(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="w-10 h-10 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={options.backgroundColor}
                      onChange={(e) => setOptions(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="flex-1 px-3 py-2 rounded-lg border border-border/40 text-sm font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Gradient Toggle */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.hasGradient}
                  onChange={(e) => setOptions(prev => ({ ...prev, hasGradient: e.target.checked }))}
                  className="w-5 h-5 rounded border-border/40"
                />
                <span className="text-sm font-medium">Enable Gradient</span>
              </label>
              
              {options.hasGradient && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground">Gradient Start</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={options.gradientColor1}
                        onChange={(e) => setOptions(prev => ({ ...prev, gradientColor1: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={options.gradientColor1}
                        onChange={(e) => setOptions(prev => ({ ...prev, gradientColor1: e.target.value }))}
                        className="flex-1 px-3 py-2 rounded-lg border border-border/40 text-sm font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Gradient End</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={options.gradientColor2}
                        onChange={(e) => setOptions(prev => ({ ...prev, gradientColor2: e.target.value }))}
                        className="w-10 h-10 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={options.gradientColor2}
                        onChange={(e) => setOptions(prev => ({ ...prev, gradientColor2: e.target.value }))}
                        className="flex-1 px-3 py-2 rounded-lg border border-border/40 text-sm font-mono"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Regenerate Button */}
            <button
              onClick={generateQRCode}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
              Regenerate QR Code
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
